import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import tokens from '@veneer/tokens';
import GenericChart from '../GenericChart';
import { mediaQueries } from '../../../helpers/mixins/mediaQueries';
import useMediaQuery from '../../../hooks/useMediaQuery';
import {
  buildTooltipTitleMapping,
  tooltipFormatter,
} from '../utils';

const SERIES_IDS = {
  COLOR: 0,
  BLACK_AND_WHITE: 1,
  AVERAGE: 2,
};

const PrintPageChart = ({
  chartData,
  error,
  loading,
  onRetry,
  selectedYear,
  pointStartAverage,
  i18n,
  noData,
}) => {
  const [sharedTooltip, setSharedTooltip] = useState(true);
  const isMobile = useMediaQuery(mediaQueries.xs);

  const isDeviceSupportTouch = ('ontouchstart' in window)
                              || (navigator.maxTouchPoints > 0)
                              || (navigator.msMaxTouchPoints > 0);

  const colorsDefault = useMemo(() => ((['#404040', '#8C3EBF', '#FFAC4D'])), []);
  const colorsHighlighted = useMemo(() => ((['#545454', '#B655F5'])), []);

  const legendTitles = useMemo(() => ({
    [SERIES_IDS.AVERAGE]: i18n.legend.average,
    [SERIES_IDS.BLACK_AND_WHITE]: i18n.legend.blackAndWhite,
    [SERIES_IDS.COLOR]: i18n.legend.color,
  }), [
    i18n.legend.average,
    i18n.legend.blackAndWhite,
    i18n.legend.color,
  ]);

  const tooltipXAxisMapping = useMemo(() => buildTooltipTitleMapping(
    i18n.xAxis.categories, i18n.tooltip.categories,
  ),
  [i18n.xAxis.categories, i18n.tooltip.categories]);

  const tooltip = useMemo(() => ({
    shared: sharedTooltip,
    followPointer: sharedTooltip,
    formatter() {
      // eslint-disable-next-line react/no-this-in-sfc
      const chart = this;
      return tooltipFormatter({
        chart,
        i18n,
        legendTitles,
        defaultTitle: tooltipXAxisMapping[chart.x],
        isDeviceTouch: isDeviceSupportTouch,
      });
    },
  }), [
    i18n,
    isDeviceSupportTouch,
    legendTitles,
    tooltipXAxisMapping,
    sharedTooltip,
  ]);

  const numCategories = i18n.xAxis.categories.length;

  const options = useMemo(() => ({
    plotOptions: {
      column: {
        pointWidth: 16,
      },
      series: {
        stacking: 'normal',
        marker: {
          radius: 2,
          fillColor: '#FFFFFF',
          lineWidth: 1,
          lineColor: null, // inherit from series
        },
      },
    },
    yAxis: {
      ...(chartData.average === null || chartData.average === 0 ? {
        minRange: 0.1,
        tickInterval: 1,
      } : {}),
      min: 0,
      max: isMobile ? chartData?.maxAxisY : null,
      title: {
        text: i18n.yAxis,
      },
    },
    xAxis: {
      min: 0,
      max: isMobile ? 4 : numCategories,
      scrollbar: {
        enabled: isMobile,
      },
      categories: i18n.xAxis.categories,
    },
    series: [
      {
        id: SERIES_IDS.AVERAGE,
        legendIndex: 2,
        type: 'line',
        name: i18n.legend.average,
        pointStart: pointStartAverage,
        data: chartData.blackAndWhite.slice(pointStartAverage).map(val => (val === null ? null : chartData.average)),
        color: colorsDefault[2],
        zIndex: 1,
        stickyTracking: false,
        isTooltipAverage: true,
        selectedYear,
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: true,
            },
          },
        },
        events: {
          mouseOver() {
            setSharedTooltip(false);
          },
          mouseOut() {
            setSharedTooltip(true);
          },
        },
      },
      {
        id: SERIES_IDS.COLOR,
        stickyTracking: false,
        legendIndex: 1,
        borderRadiusTopLeft: chartData.average ? tokens.borderRadius2 : 0,
        borderRadiusTopRight: chartData.average ? tokens.borderRadius2 : 0,
        name: i18n.legend.color,
        data: chartData.color,
        type: 'column',
        color: colorsDefault[1],
        events: {
          mouseOver() {
            // eslint-disable-next-line react/no-this-in-sfc
            this.update({
              color: colorsHighlighted[1],
            });
          },
          mouseOut() {
            // eslint-disable-next-line react/no-this-in-sfc
            this.update({
              color: colorsDefault[1],
            });
          },
        },
      },
      {
        id: SERIES_IDS.BLACK_AND_WHITE,
        legendIndex: 0,
        borderRadiusTopLeft: 0,
        borderRadiusTopRight: 0,
        name: i18n.legend.blackAndWhite,
        data: chartData.blackAndWhite,
        type: 'column',
        color: colorsDefault[0],
        events: {
          mouseOver() {
            // eslint-disable-next-line react/no-this-in-sfc
            this.update({
              color: colorsHighlighted[0],
            });
          },
          mouseOut() {
            // eslint-disable-next-line react/no-this-in-sfc
            this.update({
              color: colorsDefault[0],
            });
          },
        },
      },
    ],
  }), [
    chartData?.maxAxisY,
    chartData.average,
    chartData.blackAndWhite,
    chartData.color,
    colorsDefault,
    colorsHighlighted,
    i18n.legend.average,
    i18n.legend.blackAndWhite,
    i18n.legend.color,
    i18n.xAxis.categories,
    i18n.yAxis,
    isMobile,
    numCategories,
    pointStartAverage,
    selectedYear,
  ]);

  return (
    <GenericChart
      data-testid="usage-print-page"
      error={error}
      loading={loading}
      onRetry={onRetry}
      options={options}
      tooltip={tooltip}
      noData={noData}
      i18n={{
        error: i18n.error,
        retry: i18n.retry,
        noDataAvailable: i18n.noDataAvailable,
      }}
    />
  );
};

PrintPageChart.defaultProps = {
  noData: false,
  pointStartAverage: 0,
};

PrintPageChart.propTypes = {
  chartData: PropTypes.shape({
    blackAndWhite: PropTypes.arrayOf(PropTypes.number).isRequired,
    color: PropTypes.arrayOf(PropTypes.number).isRequired,
    average: PropTypes.number.isRequired,
    maxAxisY: PropTypes.number,
  }).isRequired,
  error: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  noData: PropTypes.bool,
  onRetry: PropTypes.func.isRequired,
  selectedYear: PropTypes.number.isRequired,
  pointStartAverage: PropTypes.number,
  i18n: PropTypes.shape({
    legend: PropTypes.shape({
      average: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      blackAndWhite: PropTypes.string.isRequired,
    }),
    xAxis: PropTypes.shape({
      categories: PropTypes.arrayOf(PropTypes.string).isRequired,
    }).isRequired,
    yAxis: PropTypes.string.isRequired,
    tooltip: PropTypes.shape({
      categories: PropTypes.arrayOf(PropTypes.string).isRequired,
      total: PropTypes.string.isRequired,
      unit: PropTypes.string,
      titleFormatter: PropTypes.func,
      averageTitleFormatter: PropTypes.func,
    }).isRequired,
    error: PropTypes.string.isRequired,
    retry: PropTypes.string.isRequired,
    noDataAvailable: PropTypes.shape({
      title: PropTypes.string.isRequired,
      subTitle: PropTypes.string.isRequired,
    }),
  }).isRequired,
};

export default PrintPageChart;
